import { Entity } from '../entity';
import { EvidenceExceptionTag, EvidenceExceptionType } from '../shared/evidence.exception.tag';
import { LabelEntity, LabelType } from '../shared/label';
import { EvidenceEnforcement } from './evidence.enforcement';
import { isEvidenceExplained, toEvidenceExceptionType } from './evidence.exception.type';
import { Task } from './task';
import { findMissingTaskEvidence } from './task.completion.gate';
import { TASK_EVIDENCE_RESPONSE_FIELDS, TASK_REQUIREMENT_FLAG_FIELDS, TaskEvidenceKind } from './task.evidence';
import { TASK_EVIDENCE_OUTCOME_FIELD } from './task.evidence.outcome';
import { isStampedTaskResolved } from './task.status.policy';

/** `reason` is the tag as it was when chosen and `explainedTypes` what it covered then, so a later edit to the tag or the task's evidence never widens it. */
export interface TaskEvidenceException {
    reason: LabelEntity;
    explainedTypes: EvidenceExceptionType[];
    comment?: string;
    recordedBy: Entity;
    recordedOn: Date;
}

/** The only fields a client sends; who and when come from the server. */
export interface TaskEvidenceExceptionRequest {
    reasonId: string;
    comment?: string;
}

export const TASK_EVIDENCE_EXCEPTION_FIELD = 'evidenceException';

/** Required (allow exceptions) sections still missing, judged as the completion gate judges; the only evidence an exception can explain. */
export function findExceptableTaskEvidence(task: Partial<Task>): TaskEvidenceKind[] {

    return findMissingTaskEvidence(task)
        .filter(shortfall => shortfall.level === EvidenceEnforcement.RequiredExceptionAllowed)
        .map(shortfall => shortfall.kind);
}

/** True when no missing evidence is left of a type the exception explains; removing evidence later must not bring it back, so only a clear is ever derived. */
export function isTaskEvidenceExceptionSpent(task: Partial<Task> | null | undefined): boolean {

    return !!task?.evidenceException && !findMissingTaskEvidence(task).some(shortfall => isEvidenceExplained(task.evidenceException, shortfall));
}

/** The missing Required (allow exceptions) types this tag is set up for: what an exception recorded now would explain. */
export function resolveExplainedTypes(task: Partial<Task>, tag: EvidenceExceptionTag | null | undefined): EvidenceExceptionType[] {

    const missing = new Set(findExceptableTaskEvidence(task).map(toEvidenceExceptionType));

    return Object.values(EvidenceExceptionType).filter(type => missing.has(type) && !!tag?.data?.evidenceTypes?.includes(type));
}

const MISSING_EVIDENCE_INPUT_FIELDS: readonly string[] = [...TASK_EVIDENCE_RESPONSE_FIELDS, ...TASK_REQUIREMENT_FLAG_FIELDS];

/** Whether a write may change what evidence is missing: a response or a requirement, whole or by a dotted path such as `afterPhotos.0`; a writer that answers yes must call the after-evidence-change seam. */
export function mayChangeMissingEvidence(patch: object | null | undefined): boolean {

    return Object.keys(patch ?? {}).some(key => MISSING_EVIDENCE_INPUT_FIELDS.some(field => key === field || key.startsWith(`${field}.`)));
}

// The rest of the outcome's inputs (`TASK_EVIDENCE_OUTCOME_INPUT_FIELDS`): status, active, the exception, the form template and the links; a template-only PATCH otherwise kept a stale Captured.
const OUTCOME_ONLY_INPUT_FIELDS: readonly string[] = ['status', 'taskStatus', 'active', TASK_EVIDENCE_EXCEPTION_FIELD, 'formTemplate', 'links'] satisfies readonly (keyof Task)[];

/** Whether a write may change the stored evidence outcome; a writer that answers yes must call the after-evidence-change seam. */
export function mayChangeEvidenceOutcome(patch: object | null | undefined): boolean {

    return mayChangeMissingEvidence(patch) || Object.keys(patch ?? {}).some(key => OUTCOME_ONLY_INPUT_FIELDS.some(field => key === field || key.startsWith(`${field}.`)));
}

/** Why the task's exception may not be recorded, replaced or removed right now, or undefined. */
export function findTaskEvidenceExceptionLock(task: Partial<Task> | null | undefined): string | undefined {

    return isStampedTaskResolved(task) ? 'The evidence exception of a closed task cannot be changed' : undefined;
}

/** Why this tag cannot explain this task's missing evidence, or undefined; checked only when recorded, so a later edit to the tag never voids it. */
export function findTaskEvidenceExceptionRefusal(task: Partial<Task>, tag: EvidenceExceptionTag | null | undefined): string | undefined {

    const lock = findTaskEvidenceExceptionLock(task);
    if (lock) return lock;

    const missing = findExceptableTaskEvidence(task);
    if (!missing.length) return 'No Required (allow exceptions) evidence is missing on this task';

    if (!tag || tag.type !== LabelType.EvidenceException || tag.active === false) return 'The reason is not an active evidence exception reason';

    return resolveExplainedTypes(task, tag).length ? undefined : 'The reason is not set up for any of the missing evidence types';
}

const SERVER_OWNED_EVIDENCE_FIELDS: readonly string[] = [TASK_EVIDENCE_EXCEPTION_FIELD, TASK_EVIDENCE_OUTCOME_FIELD];

/** Generic task writes drop the exception and the outcome with any dotted path under them, since a request could otherwise explain its own missing evidence or claim an outcome. */
export function withoutServerOwnedEvidence<T extends object>(patch: T | null | undefined): Partial<T> {

    const kept: Partial<T> = { ...patch };
    Object.keys(kept).filter(isServerOwnedEvidencePath).forEach(field => Reflect.deleteProperty(kept, field));

    return kept;
}

/** The exception, the outcome or a dotted path under either, as an update key or an attachment route's target field. */
export function isServerOwnedEvidencePath(field: string | null | undefined): boolean {

    return SERVER_OWNED_EVIDENCE_FIELDS.some(owned => field === owned || !!field?.startsWith(`${owned}.`));
}

/** The snapshot stored on the task, built from the stored task and tag only; the tag's `data` is left out because `explainedTypes` already fixes what it covers. */
export function buildTaskEvidenceException(task: Partial<Task>, tag: EvidenceExceptionTag, comment: string | null | undefined, recordedBy: Entity, recordedOn: Date): TaskEvidenceException {

    const trimmed = comment?.trim();

    return {
        reason: { _id: tag._id, name: tag.name, color: tag.color, icon: tag.icon, type: tag.type },
        explainedTypes: resolveExplainedTypes(task, tag),
        ...(trimmed ? { comment: trimmed } : {}),
        recordedBy,
        recordedOn,
    };
}
